import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import { toast } from 'react-toastify';

import {
  createLearnedAnswer,
  deleteLearnedAnswer,
  listAssistantConversations,
  listLearnedAnswers,
} from '../../services/aiApi';
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  PageHeader,
  PageLayout,
  SectionCard,
} from '../../components/ui';

const QUESTION_MAX = 2000;
const ANSWER_MAX = 8000;
const ANSWER_PREVIEW = 160;

/** Best-effort, never-throws formatter for the stored ISO timestamp. */
function formatWhen(ts) {
  if (!ts) return '—';
  try {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return String(ts);
    return d.toLocaleString();
  } catch {
    return String(ts);
  }
}

/** Truncate long text with an ellipsis (display only). */
function truncate(text, max) {
  const value = String(text ?? '');
  if (value.length <= max) return value;
  return `${value.slice(0, max).trimEnd()}…`;
}

export default function AssistantTrainingPage() {
  const [conversations, setConversations] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(true);
  const [conversationsError, setConversationsError] = useState(false);

  const [answers, setAnswers] = useState([]);
  const [answersLoading, setAnswersLoading] = useState(true);
  const [answersError, setAnswersError] = useState(false);

  // Per-row "expanded answer" toggles in the conversations table.
  const [expandedRows, setExpandedRows] = useState({});

  // Promote / add-answer dialog.
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogQuestion, setDialogQuestion] = useState('');
  const [dialogAnswer, setDialogAnswer] = useState('');
  const [saving, setSaving] = useState(false);

  // Delete-confirmation dialog for a local answer.
  const [pendingDelete, setPendingDelete] = useState(null); // { doc, question } | null
  const [deleting, setDeleting] = useState(false);

  const fetchConversations = useCallback(async () => {
    setConversationsLoading(true);
    setConversationsError(false);
    try {
      const data = await listAssistantConversations();
      setConversations(Array.isArray(data?.conversations) ? data.conversations : []);
    } catch {
      setConversationsError(true);
      setConversations([]);
    } finally {
      setConversationsLoading(false);
    }
  }, []);

  const fetchAnswers = useCallback(async () => {
    setAnswersLoading(true);
    setAnswersError(false);
    try {
      const data = await listLearnedAnswers();
      setAnswers(Array.isArray(data?.answers) ? data.answers : []);
    } catch {
      setAnswersError(true);
      setAnswers([]);
    } finally {
      setAnswersLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConversations();
    fetchAnswers();
  }, [fetchConversations, fetchAnswers]);

  const toggleRow = (key) => {
    setExpandedRows((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const openPromoteDialog = (question, answer) => {
    setDialogQuestion(String(question ?? ''));
    setDialogAnswer(String(answer ?? ''));
    setDialogOpen(true);
  };

  const openAddDialog = () => {
    setDialogQuestion('');
    setDialogAnswer('');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
  };

  const canSave =
    dialogQuestion.trim().length > 0 &&
    dialogAnswer.trim().length > 0 &&
    dialogQuestion.length <= QUESTION_MAX &&
    dialogAnswer.length <= ANSWER_MAX &&
    !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await createLearnedAnswer({
        question: dialogQuestion.trim(),
        answer: dialogAnswer.trim(),
      });
      toast.success('Saved to the local knowledge base.');
      setDialogOpen(false);
      fetchAnswers();
    } catch (e) {
      toast.error(
        e?.response?.data?.message || 'Could not save this answer. Please try again.',
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete?.doc) return;
    setDeleting(true);
    try {
      const data = await deleteLearnedAnswer(pendingDelete.doc);
      if (data?.ok) {
        toast.success('Local answer removed.');
        setAnswers((prev) => prev.filter((a) => a.doc !== pendingDelete.doc));
      } else {
        toast.error('That answer could not be found.');
      }
      setPendingDelete(null);
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not remove this answer.');
    } finally {
      setDeleting(false);
    }
  };

  const renderConversations = () => {
    if (conversationsLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      );
    }

    if (conversationsError) {
      return (
        <ErrorState
          compact
          title="Could not load conversations"
          description="The assistant log could not be read right now. This does not affect the live assistant."
          onRetry={fetchConversations}
        />
      );
    }

    if (conversations.length === 0) {
      return (
        <EmptyState
          compact
          icon={AutoAwesomeOutlinedIcon}
          title="No conversations yet"
          description="Once employees start asking the assistant questions, the most recent ones will appear here for review."
        />
      );
    }

    return (
      <TableContainer>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>When</TableCell>
              <TableCell sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>Employee</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Question</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Answer</TableCell>
              <TableCell sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>Source</TableCell>
              <TableCell sx={{ fontWeight: 800, whiteSpace: 'nowrap' }} align="right">
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {conversations.map((row, index) => {
              const key = `${row.ts || 'row'}-${index}`;
              const personal = !!row.used_personal_data;
              const expanded = !!expandedRows[key];
              const answerText = String(row.answer ?? '');
              const isLong = answerText.length > ANSWER_PREVIEW;

              return (
                <TableRow key={key} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                    <Typography variant="caption" color="text.secondary">
                      {formatWhen(row.ts)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                    {row.employee_code || '—'}
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top', maxWidth: 280 }}>
                    <Typography variant="body2">{row.question || '—'}</Typography>
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top', maxWidth: 360 }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {expanded ? answerText : truncate(answerText, ANSWER_PREVIEW)}
                    </Typography>
                    {isLong ? (
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => toggleRow(key)}
                        sx={{ px: 0.5, minWidth: 0, mt: 0.25 }}
                      >
                        {expanded ? 'Show less' : 'Show more'}
                      </Button>
                    ) : null}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      <Chip
                        size="small"
                        label={row.source === 'local' ? 'local' : 'claude'}
                        color={row.source === 'local' ? 'success' : 'info'}
                        variant={row.source === 'local' ? 'filled' : 'outlined'}
                      />
                      {personal ? (
                        <Chip
                          size="small"
                          label="Personal"
                          color="warning"
                          variant="outlined"
                          icon={<LockOutlinedIcon sx={{ fontSize: 14 }} />}
                        />
                      ) : null}
                    </Stack>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                    {personal ? (
                      <Tooltip title="Personalized answers can't be cached.">
                        <span>
                          <Button size="small" variant="outlined" disabled>
                            Promote → local answer
                          </Button>
                        </span>
                      </Tooltip>
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => openPromoteDialog(row.question, row.answer)}
                      >
                        Promote → local answer
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderAnswers = () => {
    if (answersLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      );
    }

    if (answersError) {
      return (
        <ErrorState
          compact
          title="Could not load local answers"
          description="The local knowledge base could not be read right now."
          onRetry={fetchAnswers}
        />
      );
    }

    if (answers.length === 0) {
      return (
        <EmptyState
          compact
          icon={StorageOutlinedIcon}
          title="No local answers yet"
          description="Promote a good general answer from a conversation, or add one manually, so the assistant can answer it instantly with no AI cost."
          actionLabel="Add answer"
          onAction={openAddDialog}
        />
      );
    }

    return (
      <List disablePadding>
        {answers.map((a) => (
          <ListItem
            key={a.doc}
            divider
            alignItems="flex-start"
            secondaryAction={
              <Tooltip title="Delete">
                <span>
                  <IconButton
                    edge="end"
                    color="error"
                    onClick={() => setPendingDelete({ doc: a.doc, question: a.question })}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </span>
              </Tooltip>
            }
          >
            <ListItemText
              primary={a.question}
              primaryTypographyProps={{ fontWeight: 700 }}
              secondary={a.answer}
              secondaryTypographyProps={{
                sx: { whiteSpace: 'pre-wrap', mt: 0.5 },
                color: 'text.secondary',
              }}
            />
          </ListItem>
        ))}
      </List>
    );
  };

  return (
    <PageLayout maxWidth="lg" backgroundAccent>
      <PageHeader
        overline="HR Assistant"
        title="Assistant Training"
        subtitle="Review what employees asked the assistant, and promote good GENERAL answers into the local knowledge base so they're answered instantly on-server (no AI cost). Personalized answers (someone's leave balance, request status, etc.) cannot be promoted."
        actions={(
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              fetchConversations();
              fetchAnswers();
            }}
          >
            Refresh
          </Button>
        )}
      />

      <Alert severity="info" icon={<SchoolOutlinedIcon />} sx={{ mb: 2.5 }}>
        Only general answers about company policy or process should be promoted. Anything that used
        an employee's personal data is flagged with a <strong>Personal</strong> chip and cannot be
        cached.
      </Alert>

      <Stack spacing={2.5}>
        <SectionCard
          title="Recent conversations"
          subheader={
            !conversationsLoading && !conversationsError && conversations.length
              ? `${conversations.length} most recent`
              : undefined
          }
          disablePadding
        >
          {renderConversations()}
        </SectionCard>

        <SectionCard
          title="Local answers"
          subheader={
            !answersLoading && !answersError && answers.length
              ? `${answers.length} curated answer${answers.length === 1 ? '' : 's'}`
              : undefined
          }
          action={(
            <Button
              size="small"
              variant="contained"
              startIcon={<AddCircleOutlineIcon />}
              onClick={openAddDialog}
            >
              Add answer
            </Button>
          )}
        >
          {renderAnswers()}
        </SectionCard>
      </Stack>

      {/* Promote / Add dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>Local answer</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              The assistant will return this answer instantly (no AI cost) when an employee asks a
              closely matching question. Only use it for general, non-personalized answers.
            </Typography>
            <TextField
              label="Question"
              value={dialogQuestion}
              onChange={(e) => setDialogQuestion(e.target.value)}
              fullWidth
              required
              multiline
              minRows={2}
              maxRows={4}
              inputProps={{ maxLength: QUESTION_MAX }}
              helperText={`${dialogQuestion.length} / ${QUESTION_MAX}`}
            />
            <TextField
              label="Answer"
              value={dialogAnswer}
              onChange={(e) => setDialogAnswer(e.target.value)}
              fullWidth
              required
              multiline
              minRows={5}
              maxRows={16}
              inputProps={{ maxLength: ANSWER_MAX }}
              helperText={`${dialogAnswer.length} / ${ANSWER_MAX}`}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button onClick={closeDialog} disabled={saving} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!canSave}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!pendingDelete}
        title="Remove local answer"
        description={
          pendingDelete?.question
            ? `Remove the curated answer for "${truncate(pendingDelete.question, 80)}"? The assistant will fall back to the AI for this question.`
            : 'Remove this curated answer?'
        }
        confirmLabel="Remove"
        confirmColor="error"
        loading={deleting}
        onConfirm={handleDelete}
        onClose={() => {
          if (!deleting) setPendingDelete(null);
        }}
      />
    </PageLayout>
  );
}
